import { getIPlan } from '@/api/cart';
import { getPackages } from '@/api/subscriptions';
import { planName } from '@/filter';
import { ICloudService, TGetSimCardListRes, ISimCard, ILastUsedDeviceItem, ESIMCardStatus, ITotalDevice, ISimCardItem } from '@reolink-cloud/interface';
import i18n from 'LANG';
import { productsManager } from './productsManager';
import { deviceManager } from '@/view/reolink/checkout/lib/deviceManager';
import { isUnlimitedDataSize } from '@/utils/business';

export interface ITrafficData {
    subscriptionTrafficTotal: number;
    subscriptionTrafficUsed: number;
    addOnTrafficTotal: number;
    addOnTrafficUsed: number;
    isUnlimitedMain: boolean;
    isUnlimitedAddOn: boolean;
}

export class SimManager {

    public selected = false;

    public trafficData: ITrafficData = {
        subscriptionTrafficTotal: 0,
        subscriptionTrafficUsed: 0,
        addOnTrafficTotal: 0,
        addOnTrafficUsed: 0,
        isUnlimitedAddOn: false,
        isUnlimitedMain: false
    };

    public constructor( public readonly data: ISimCard, services: TGetSimCardListRes['services'] = {}) {

        if ( !data.services ) {

            data.services = data.cloudServices?.reduce(( result, currentId ) => {

                result[currentId] = services[currentId];
                return result;

            }, {});

        }

    }

    public get serviceArr(): ICloudService[] {

        return Object.values( this.data.services ?? {});

    }

    public get device(): ILastUsedDeviceItem|undefined {

        return this.data.lastUsedDevice ?? undefined;

    }

    public get isCardSuspendForCountry(): boolean {

        return this.data.simCard?.state === ESIMCardStatus.SUSPENDED && this.currentSubscription?.status === 'active' && this.data.simCard?.trafficLimitStatus === 'limited'
        && this.data.simCard.mode !== 'admin';

    }

    public get lastUsedCountry(): string {

        return this.data.simCard?.trafficCountryName ?? '';

    }

    public get trafficLimitStatus(): ISimCardItem['trafficLimitStatus'] | undefined  {

        return this.data.simCard?.trafficLimitStatus;
    
    }

    private async initPlanInfo(): Promise<void> {

        await Promise.all(( this.serviceArr  ).map( service => getIPlan( service.plan ).then( planInfo => service.planInfo = planInfo )));

    }

    public get isRetired(): boolean {

        return [ESIMCardStatus.RETIRED, ESIMCardStatus.RETIRING].includes( this.data.simCard?.state ?? ESIMCardStatus.ACTIVE );

    }

    public async init( include?: {
        trafficData: boolean;
    }): Promise<void> {

        await this.initPlanInfo();
        if ( include?.trafficData ) {

            await this.getTrafficData();

        }

    }

    /** 获取当前正在生效的订阅 */
    public get currentSubscription(): ICloudService|undefined {

        return this.serviceArr.find( service => service.type === 'subscription' );

    }

    /** 获取当前订阅，不管订阅是否有效 */
    public get currentSubscriptionWithoutStatus(): ICloudService|undefined {

        return this.serviceArr.find( service => service.type === 'subscription' );

    }

    public get currentSubscriptionPlanName(): string|undefined {

        if ( this.currentSubscription?.planInfo ) {

            return planName( this.currentSubscription.planInfo );

        }

    }

    public get activeAddOns(): ICloudService[] {

        return ( this.serviceArr.filter( service => service.type === 'package' && service.effectiveStatus !== 'inactive' ) ?? [] )
            .sort(( a, b ) => parseFloat( a.id ) - parseFloat( b.id ));

    }

    public get iccid(): string {

        return this.data.simCard?.iccid ?? '';

    }

    public get simCardCode(): string {

        return this.data.simCard?.simCardCode ?? '';

    }

    public get batchCondition(): string {

        const planInfo = this.currentSubscription?.planInfo;
        if ( planInfo?.series ) {

            return `${planInfo.series.countryGroups.join( '|' ) } ${planInfo.pricing.currency}`;

        }

        return '';

    }

    public get renewAble(): boolean {

        if ( !this.currentSubscription || this.isRetired ) {

            return false;

        }
        const plan = this.currentSubscription.planInfo;
        const isFreeSub = parseFloat( plan?.pricing.salePrice ?? '0' ) === 0;

        return !isFreeSub && plan?.status !== 'disabled'
                    && !this.currentSubscription.autoRenewal
                    && !( plan?.limitations.userMaxSubscribeTimes === 1 )
                    && !( plan?.limitations.subscriptionMaxPurchaseCycles === 1 )
                    && !( plan?.limitations.simMaxSubscribeCycles === 1 );

    }

    public async getTrafficData(): Promise<void> {

        if ( this.currentSubscription?.status === 'active' ) {

            const packages = await getPackages({ subscriptionId: this.currentSubscription.id });
            let subscriptionTrafficTotal = 0;
            let subscriptionTrafficUsed = 0;
            let addOnTrafficTotal = 0;
            let addOnTrafficUsed = 0;
            for ( const packageData of packages ) {

                if ( packageData.isCurrentMainPackage ) {

                    if ( packageData.type === 'main' ) {

                        subscriptionTrafficTotal += parseInt( packageData.totalSize );
                        subscriptionTrafficUsed += parseInt( packageData.usedSize );

                    }

                }
                if ( packageData.type === 'additional' && packageData.status === 'active' ) {

                    addOnTrafficTotal += parseInt( packageData.totalSize );
                    addOnTrafficUsed += parseInt( packageData.usedSize );

                }

            }
            this.trafficData.subscriptionTrafficTotal = subscriptionTrafficTotal;
            this.trafficData.subscriptionTrafficUsed = subscriptionTrafficUsed;
            this.trafficData.addOnTrafficTotal = addOnTrafficTotal;
            this.trafficData.addOnTrafficUsed = addOnTrafficUsed;
            this.trafficData.isUnlimitedMain = isUnlimitedDataSize( this.currentSubscription?.planInfo );
            this.trafficData.isUnlimitedAddOn = this.activeAddOns.some( addOn => isUnlimitedDataSize( addOn.planInfo ?? undefined ));

        }

    }

    public get deviceString(): string {

        const currentDevice = this.data.lastUsedDevice;
        if ( currentDevice ) {

            if ( currentDevice.uid === 'unknown' ) {

                return i18n.t( 'common.unknown' ).toString();

            }
            let nickName = currentDevice.title;
            if ( !nickName || nickName === '' ) {

                nickName = currentDevice.model.title;

            }
            return i18n.t( 'my_sim.device_string', {
                nickName,
                uid: currentDevice?.uid,
                model: currentDevice?.model.title
            }).toString();

        }

        return i18n.t( 'my_sim.no_device' ).toString();

    }

    public get lockedDevice(): ITotalDevice | undefined {

        return deviceManager.getDeviceByUid( this.data.esimDevice );

    }

    public static async initSimManagers( simManagers: SimManager[] ): Promise<void> {

        //事先确保productManager中有manager们需要的plan信息，避免重复请求
        const planSet = new Set<string>();
        for ( const manager of simManagers ) {

            const planId = manager.currentSubscription?.plan;
            if ( planId ) {

                planSet.add( planId );
                for ( const addOn of manager.activeAddOns ) {

                    planSet.add( addOn.plan );

                }

            }

        }
        await Promise.all( Array.from( planSet ).map( planId => productsManager.getPlanById( planId )));

        await Promise.all( simManagers.map( simManager => simManager.init()));

        return;

    }

}
